import React, { useState, useEffect } from 'react';
import { JobDetailsFields, JobRequirementsSection } from './JobFormSections';

interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Internship';
  salary: string;
  postedDate: string;
  deadline: string;
  status: 'Open' | 'Closed' | 'Draft';
  applicants: number;
  description: string;
  requirements: string[];
  useSeafarerForm?: boolean;
}

interface JobFormProps {
  job?: Job | null;
  onSave: (job: Omit<Job, 'id' | 'applicants'>) => void;
  onCancel: () => void;
}

export const JobForm: React.FC<JobFormProps> = ({ job, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    type: 'Full-time' as 'Full-time' | 'Part-time' | 'Contract' | 'Internship',
    salary: '',
    postedDate: new Date().toISOString().split('T')[0],
    deadline: '',
    status: 'Draft' as 'Open' | 'Closed' | 'Draft',
    description: '',
    requirements: [''],
    useSeafarerForm: false
  });

  useEffect(() => {
    if (job) {
      setFormData({
        title: job.title,
        department: job.department,
        location: job.location,
        type: job.type,
        salary: job.salary,
        postedDate: job.postedDate,
        deadline: job.deadline,
        status: job.status,
        description: job.description,
        requirements: job.requirements.length > 0 ? job.requirements : [''],
        useSeafarerForm: !!job.useSeafarerForm
      });
    }
  }, [job]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRequirementChange = (index: number, value: string) => {
    const updatedRequirements = [...formData.requirements];
    updatedRequirements[index] = value;
    setFormData(prev => ({ ...prev, requirements: updatedRequirements }));
  };

  const addRequirement = () => {
    setFormData(prev => ({ ...prev, requirements: [...prev.requirements, ''] }));
  };

  const removeRequirement = (index: number) => {
    if (formData.requirements.length > 1) {
      const updatedRequirements = formData.requirements.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, requirements: updatedRequirements }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const filteredRequirements = formData.requirements.filter(req => req.trim() !== '');
    onSave({ ...formData, requirements: filteredRequirements });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {job ? 'Edit Job Posting' : 'Create New Job Posting'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <JobDetailsFields formData={formData} onInputChange={handleInputChange} />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Description *
            </label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the job responsibilities, qualifications, and other details..."
            />
          </div>

          <JobRequirementsSection
            requirements={formData.requirements}
            onRequirementChange={handleRequirementChange}
            onAddRequirement={addRequirement}
            onRemoveRequirement={removeRequirement}
          />

          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {job ? 'Update Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
